<template>
  <ChannelSection>
    <MvpLeaderboardSkeleton v-if="loading" />

    <div v-else class="flex flex-col items-center mx-5 rounded-[24px]">
      <TabsRoot
        v-model="activeTab"
        :default-value="LeaderboardLength.Weekly"
        class="flex flex-col w-full bg-surface"
        :class="{ 'rounded-3xl': !channelStore.isAsMonacoFanToken }"
      >
        <div
          class="bg-surface border-[3px] border-border-brand flex-col justify-start items-center inline-flex"
          :class="{ 'rounded-3xl': !channelStore.isAsMonacoFanToken }"
        >
          <div
            class="p-4 bg-surface self-stretch justify-between items-start gap-3 flex flex-col lg:flex-row"
            :class="{ 'rounded-t-3xl': !channelStore.isAsMonacoFanToken }"
          >
            <div class="flex flex-row gap-3">
              <div
                class="self-stretch p-1.5 pb-2 bg-fill-brand-secondary rounded-full flex-col justify-between items-center inline-flex"
              >
                <img
                  :src="communityOrChannelLogo"
                  :alt="$t('channel.mvpLeaderboard.title')"
                  class="size-8 min-w-8 rounded-full border-2 border-brand-primary"
                />
                <div class="flex-col justify-start items-center flex">
                  <Icon icon="mdi:people" class="w-4 h-4 text-[#d9d9d9]" />
                  <!--                  <div class="text-center text-white text-[10px] font-medium font-sans">-->
                  <!--                    1.2K-->
                  <!--                  </div>-->
                </div>
              </div>

              <ChannelMonthlyPrize
                mini
                size="sm"
                :leaderboard-length="activeTab"
              />
            </div>

            <div class="flex gap-3 w-full lg:w-fit">
              <div
                class="self-stretch justify-end items-start inline-flex w-full lg:w-fit"
              >
                <TabsList
                  aria-label="profile-tabs"
                  class="relative px-1 flex bg-surface rounded-3xl text-base text-basics-primary font-serif w-full lg:w-fit h-10"
                >
                  <TabsIndicator
                    class="absolute py-1 left-0 h-full bottom-0 top-0 w-[--reka-tabs-indicator-size] translate-x-[--reka-tabs-indicator-position] transition-[width,transform] duration-300"
                  >
                    <div class="bg-fill-brand h-full rounded-2xl" />
                  </TabsIndicator>

                  <TabsTrigger
                    class="uppercase px-4 text-sm h-full relative z-10 py-3 line-clamp-1 leading-none select-none outline-none cursor-pointer w-1/2 lg:w-fit whitespace-nowrap"
                    :value="LeaderboardLength.Weekly"
                  >
                    {{ $t("channel.mvpLeaderboard.tabs.weekly") }}
                  </TabsTrigger>

                  <TabsTrigger
                    class="uppercase px-4 text-sm h-full relative z-10 py-3 line-clamp-1 leading-none select-none outline-none cursor-pointer w-1/2 lg:w-fit whitespace-nowrap"
                    :value="LeaderboardLength.Monthly"
                  >
                    {{ $t("channel.mvpLeaderboard.tabs.monthly") }}
                  </TabsTrigger>
                </TabsList>
              </div>
              <ManageLeaderboardPopover
                v-if="
                  userStore.isAuthenticated &&
                  channelStore.permissions.manageChannel
                "
              />
            </div>
          </div>

          <div class="w-full">
            <ContestLeaderboard
              :loading="false"
              :ranking="ranking"
              :leaderboard-length="activeTab"
              :loading-text="$t('channel.mvpLeaderboard.loading')"
              :empty-text="$t('channel.mvpLeaderboard.empty')"
            />
          </div>

          <div
            class="self-stretch p-4 pt-0 flex-col justify-start items-start gap-2.5 flex"
          >
            <button
              class="w-full flex justify-center items-center h-10 px-4 py-1 bg-fill-brand rounded-full border-t border-white/40 text-center text-white text-sm font-medium font-sans leading-tight"
              @click="isFullLeaderboardModalOpen = true"
            >
              {{ $t("channel.mvpLeaderboard.fullLeaderboard") }}
            </button>
          </div>
        </div>

        <div
          v-if="
            userStore.isAuthenticated &&
            channelStore.hasSuperfanProduct &&
            !channelStore.isSuperfan &&
            !channelStore.isHost
          "
          class="flex flex-col md:flex-row items-stretch md:items-center justify-center gap-3 p-4"
        >
          <div class="flex-1 flex items-center gap-2">
            <CoinPile class="w-12 h-16 md:h-11 md:w-8" />
            <div class="flex flex-col">
              <h3 class="text-lg text-text-on-brand font-serif">
                {{ $t("channel.mvp.multiplier.title") }}
              </h3>
              <p class="text-text text-sm">
                {{ $t("channel.mvp.multiplier.description") }}
              </p>
            </div>
          </div>

          <UnlockPointsMultiplierButton />
        </div>
      </TabsRoot>
    </div>
  </ChannelSection>

  <LeaderboardModal
    :is-open="isFullLeaderboardModalOpen"
    @close="isFullLeaderboardModalOpen = false"
  />
</template>

<script setup lang="ts">
import { graphql as gql } from "@/generated";
import { useChannelStore } from "@/stores/channel.store";
import { useLoyaltyNotifications } from "@/features/channel/services/use-loyalty-notifications";
import { computed, ref, watch } from "vue";
import { TabsIndicator, TabsList, TabsRoot, TabsTrigger } from "reka-ui";
import ChannelMonthlyPrize from "@/components/channel/channel-monthly-prize.vue";
import { Icon } from "@iconify/vue";
import ContestLeaderboard, {
  ContestLeaderboardUserFragment,
} from "@/components/contest/contest-leaderboard.vue";
import ChannelSection from "@/components/channel/channel-section.vue";
import LeaderboardModal from "@/components/leaderboard/leaderboard-modal.vue";
import { LeaderboardLength } from "@f3/shared/leaderboard-types";
import { AppThemeKind, useThemeStore } from "@/stores/theme.store";
import CoinPile from "@/components/ui/icon/coin-pile.vue";
import UnlockPointsMultiplierButton from "@/components/ui/button/unlock-points-multiplier-button.vue";
import { useUserStore } from "@/stores/user.store";
import ManageLeaderboardPopover from "@/components/leaderboard/manage-leaderboard-popover.vue";
import { useQuery } from "@urql/vue";
import MvpLeaderboardSkeleton from "@/components/ui/skeleton/mvp-leaderboard-skeleton.vue";
import type { ResultOf } from "@graphql-typed-document-node/core";

type ContestLeaderboardUser = ResultOf<typeof ContestLeaderboardUserFragment>;

const ChannelLeaderboardDocument = gql(/* GraphQL */ `
  query channelLeaderboard(
    $channel_id: String!
    $leaderboard_length: LeaderboardLength
    $community_id: String
    $highlights: Boolean
    $start_date: DateTimeISO
    $end_date: DateTimeISO
    $page: Float
    $limit: Float
  ) {
    channelLeaderboard(
      channel_id: $channel_id
      leaderboard_length: $leaderboard_length
      community_id: $community_id
      highlights: $highlights
      start_date: $start_date
      end_date: $end_date
      page: $page
      limit: $limit
    ) {
      ... on Leaderboard {
        ranking {
          ...ContestLeaderboardUser
        }
        total_contestants
      }
    }
  }
`);

const channelStore = useChannelStore();
const themeStore = useThemeStore();
const userStore = useUserStore();
const activeTab = ref(LeaderboardLength.Weekly);
const isFullLeaderboardModalOpen = ref(false);

const {
  data: leaderboard,
  fetching,
  executeQuery: refetch,
} = useQuery({
  query: ChannelLeaderboardDocument,
  variables: computed(() => ({
    channel_id: channelStore.channelSlug!,
    leaderboard_length:
      activeTab.value === LeaderboardLength.Monthly
        ? LeaderboardLength.Monthly
        : LeaderboardLength.Weekly,
    highlights: true,
    community_id:
      themeStore.currentTheme?.kind === "community"
        ? themeStore.currentTheme?.id
        : undefined,
  })),
  pause: computed(() => !channelStore.channelSlug),
});

const ranking = computed(() => {
  const result =
    (leaderboard.value?.channelLeaderboard.__typename === "Leaderboard" &&
      leaderboard.value.channelLeaderboard.ranking) ||
    [];
  return result.map((r) => r as ContestLeaderboardUser);
});

const loyaltyNotifications = useLoyaltyNotifications();

watch(
  () => loyaltyNotifications.notifications.length,
  (newLength, oldLength) => {
    if (newLength > oldLength) refetch();
  },
);

watch(
  () => channelStore.lastLeaderboardUpdate,
  (newDate, oldDate) => {
    if ((newDate && !oldDate) || (newDate && oldDate && newDate > oldDate)) {
      refetch();
    }
  },
);

const communityOrChannelLogo = computed(() => {
  if (themeStore.currentTheme?.kind === AppThemeKind.community) {
    return (
      channelStore.channelCommunities?.find(
        (cc) => cc.id === themeStore.currentTheme?.id,
      )?.image ?? undefined
    );
  }
  return channelStore.channel?.logo ?? undefined;
});

const loading = computed(() => fetching.value || !channelStore.channel);
</script>
